<template>
  <div class="flex flex-col">
    <div class="relative flex items-center justify-center">
      <Input
        class="w-full -my-0"
        :type="showPassword ? 'text' : 'password'"
        :placeholder="placeholder"
        :autofocus="autofocus"
        :model-value="modelValue"
        :error="error"
        :autocomplete="autocomplete"
        @update:modelValue="(value) => $emit('update:modelValue', value)"
        @focus="onInputFocus"
        @blur="onInputBlur"
      />

      <EyeIcon
        :eye-type="showPassword ? 'on' : 'off'"
        class="absolute top-0 right-2
          transition-all
          h-full
          flex items-center justify-center"
        :class="{
          'text-gray-400': !focused && !error,
          'text-blue-900': focused && !error,
          'text-red-600': error
        }"
        @click="onTogglePassword"
        @mousedown="(e) => e.preventDefault()"
        @mouseup="(e) => e.preventDefault()"
      />
    </div>

    <div
      v-if="validatePassword && showPasswordPolicy && !isValidPassword"
      class="flex flex-col items-start text-left bg-gray-100 -mt-2 p-2 space-y-2"
    >
      <p class="text-base text-gray-400">
        {{ $t("message.password_policy_title") }}
      </p>
      <ul>
        <li
          v-for="(item, index) in passwordRequirementsList()"
          :key="index"
          class="flex items-center space-x-1 text-gray-600"
        >
          <CheckmarkIcon :checked="item.validator" />
          <p>{{ item.text }}</p>
        </li>
      </ul>
      <div class="w-full bg-gray-300 h-1 rounded-lg">
        <div
          class="bg-green-600 h-1 rounded-lg"
          :class="statusBarPercentage === 0 ? 'w-0' : `w-${statusBarPercentage}/6`"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Input from './Input.vue';
import CheckmarkIcon from './CheckmarkIcon.vue';
import EyeIcon from './EyeIcon.vue';
import { mapGetters } from 'vuex';

export default defineComponent({
  name: 'PasswordInput',
  components: {
    Input,
    CheckmarkIcon,
    EyeIcon
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    autocomplete: {
      type: String
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    isValid: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    validatePassword: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'update:isValid'],
  data () {
    return {
      showPasswordPolicy: false,
      showPassword: false,
      focused: false
    };
  },
  watch: {
    isValidPassword (isValid: boolean): void {
      if (this.validatePassword) {
        this.$emit('update:isValid', isValid);
      }
    }
  },
  methods: {
    onTogglePassword () {
      this.showPassword = !this.showPassword;
    },
    onInputFocus () {
      this.focused = true;
      this.showPasswordPolicy = true;
    },
    onInputBlur () {
      this.focused = false;
      if (this.isValidPassword) {
        this.showPasswordPolicy = false;
      }
    },
    passwordRequirementsList () {
      return [
        {
          text: this.$t('message.password_policy_spaces'),
          validator: this.passwordMustNotStartOrEndWithSpaces
        },
        {
          text: this.$t('message.password_policy_length'),
          validator: this.passwordHasValidLenght
        },
        {
          text: this.$t('message.password_policy_lowercase'),
          validator: this.passwordHasLowercase
        },
        {
          text: this.$t('message.password_policy_uppercase'),
          validator: this.passwordHasUppercase
        },
        {
          text: this.$t('message.password_policy_number'),
          validator: this.passwordHasNumber
        },
        {
          text: this.$t('message.password_policy_special'),
          validator: this.passwordHasSpecialChar
        }
      ];
    }

  },
  computed: {
    ...mapGetters([
      'passwordPolicy'
    ]),
    isValidPassword (): boolean {
      return this.passwordMustNotStartOrEndWithSpaces &&
        this.passwordHasValidLenght &&
        this.passwordHasLowercase &&
        this.passwordHasUppercase &&
        this.passwordHasNumber &&
        this.passwordHasSpecialChar;
    },
    statusBarPercentage (): number {
      let percentage = 0;
      if (this.passwordMustNotStartOrEndWithSpaces) {
        percentage++;
      }
      if (this.passwordHasValidLenght) {
        percentage++;
      }
      if (this.passwordHasLowercase) {
        percentage++;
      }
      if (this.passwordHasUppercase) {
        percentage++;
      }
      if (this.passwordHasNumber) {
        percentage++;
      }
      if (this.passwordHasSpecialChar) {
        percentage++;
      }
      return percentage;
    },
    passwordMustNotStartOrEndWithSpaces (): boolean {
      return this.passwordPolicy.noStartingSpacesRgx.test(this.modelValue) &&
        this.passwordPolicy.noEndingSpacesRgx.test(this.modelValue);
    },
    passwordHasValidLenght (): boolean {
      return this.modelValue?.length >= this.passwordPolicy.length;
    },
    passwordHasLowercase (): boolean {
      return this.passwordPolicy.lowerCaseRgx.test(this.modelValue);
    },
    passwordHasUppercase (): boolean {
      return this.passwordPolicy.upperCaseRgx.test(this.modelValue);
    },
    passwordHasNumber (): boolean {
      return this.passwordPolicy.numberRgx.test(this.modelValue);
    },
    passwordHasSpecialChar (): boolean {
      return this.passwordPolicy.specialCharRgx.test(this.modelValue);
    }
  }
});
</script>

<style>
</style>
